import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LinearProgress,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import NavigationBar from '../components/NavigationBar';
import CollapsibleSidebar from '../components/CollapsibleSidebar';
import ResultsTable from '../components/ResultsTable';
import RowDetails from '../components/RowDetails';
// import ResultsVisualization from '../components/ResultsVisualization';
import ResultsSummary from '../components/ResultsSummary';
import RetrievalResults from '../components/RetrievalResults';
import { downloadCSV } from '../utils/DownloadCSV';
import { getAccessToken } from '../utils/auth';
import { useResultsData } from '../hooks/useResultsData'; 

function ResultsPage() {
  console.log("I AM IN THE RESULTS PAGE")
  const { batch_id } = useParams();
  const navigate = useNavigate();

  // User details
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [tenantId, setTenantId] = useState('');
  const {
    results,
    retrievalResults,
    resultsSummary,
    isCreatingTasks,
    loading,
    error,
    progress,
    retry,
    setRetry,
  } = useResultsData(batch_id);
  
  const [selectedRow, setSelectedRow] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);


  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };



  const handleLogout = () => {
    sessionStorage.removeItem('access_token');
    window.location.reload();
  };

  const handleDownload = () => {
    console.log("Download button clicked!");
    console.log("Results Data:", results);
    downloadCSV(results, batch_id);
  };

  const handleRowClick = (row) => {
    setSelectedRow((prev) =>
      prev && prev.user_query === row.user_query ? null : row
    );
  };
  const handleCloseDetails = () => setSelectedRow(null);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    document.title = 'Batch Results';
    const fetchUserDetails = async () => {
      try {
        const accessToken = getAccessToken();
        if (!accessToken) {
          throw new Error('No access token found');
        }
        console.log("REACT_APP_BACKEND_URL:", process.env.REACT_APP_BACKEND_URL)
        const response = await fetch(process.env.REACT_APP_BACKEND_URL + 'get_user_details', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ access_token: accessToken }),
        });
        if (!response.ok) throw new Error(`Error ${response.status}`);
        console.log("user data")
        const data = await response.json();
        console.log(data)
        setUserName(data.user_name);
        setUserEmail(data.user_email);
        setTenantId(data.tenant_id);
      } catch (err) {
        console.error('Failed to fetch user details:', err.message);
        alert('Session expired. Please log in again.');
        navigate('/');
      }
    };

    fetchUserDetails();
  }, [navigate]);


  const getProgressLabel = () => {
    if (progress === 0) return 'Starting...';
    if (progress < 50) return 'Processing...';
    if (progress < 100) return 'Almost done...';
    return 'Completed!';
  };

  // Debugging
  console.log('isCreatingTasks:', isCreatingTasks);
  console.log('loading:', loading);
  console.log('results.length:', results.length);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <CollapsibleSidebar />

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, marginLeft: '20px' }}>
        <NavigationBar
          userName={userName}
          userEmail={userEmail}
          tenantId={tenantId}
          onLogout={handleLogout}
        />


        {/* Show loading bar while creating/looking for the master task that creates the batch*/}
        {!loading && !error && results.length == 0 && (
        <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
          <Paper
            elevation={3}
            sx={{
              padding: '20px',
              width: '100%',
              maxWidth: '500px',
              margin: 'auto',
              borderRadius: '10px',
            }}
          >
            {/* When creating tasks or fetching results */}
            {(isCreatingTasks || loading) ? (
              <>
                <Typography variant="h6" gutterBottom>
                  {isCreatingTasks ? 'Preparing tasks... Please wait.' : getProgressLabel()}
                </Typography>

                {!isCreatingTasks && loading && (
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{
                        height: '12px',
                        width: '100%',
                        borderRadius: '6px',
                        backgroundColor: '#e0e0e0',
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        textAlign: 'center',
                        fontWeight: 'bold',
                      }}
                    >
                      {progress}%
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              // Fallback spinner when state is "idle" but results are still empty
              <>
                <Typography variant="h6" gutterBottom>
                  Finalizing results... Hang tight!
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <LinearProgress sx={{ width: '80%' }} />
                </Box>
              </>
            )}
          </Paper>
        </Box>
      )}

        {/* Show task creation or progress bar while processing */}
        {(isCreatingTasks || loading) && (
          <Box sx={{ textAlign: 'center', marginTop: '20px' }}>
            <Paper
              elevation={3}
              sx={{
                padding: '20px',
                width: '100%',
                maxWidth: '500px',
                margin: 'auto',
                borderRadius: '10px',
              }}
            >
              <Typography variant="h6" gutterBottom>
                {isCreatingTasks ? 'Creating tasks... Please wait.' : getProgressLabel()}
              </Typography>
              {!isCreatingTasks && loading && (
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                      height: '12px',
                      width: '100%',
                      borderRadius: '6px',
                      backgroundColor: '#e0e0e0',
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {progress}%
                  </Typography>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Show tabs only when results are ready */}
        {!loading && !error && results.length > 0 && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginTop: '20px' }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="Table View" />
                <Tab label="Retrieval" disabled={!retrievalResults}/>
                <Tab label="Summary" disabled = {!ResultsSummary}/>
              </Tabs>
            </Box>

            {/* Table View */}
            {activeTab === 0 && (
<Paper
  elevation={3}
  sx={{
    width: '90%',
    margin: '20px auto',
    padding: '24px',
    borderRadius: '10px',
  }}
>
  {
  results=="Error" ?  (
    <div style={{ color: 'red', padding: '1rem' }}>
    An error occurred getting your task ready. Wait a few minutes and try again.
  </div>
  )  : (
    <>

<ResultsTable
  results={results}
  page={page}
  rowsPerPage={10}
  onPageChange={handleChangePage}
  onRowsPerPageChange={handleChangeRowsPerPage}
  onRowClick={handleRowClick}
  onDownloadCSV={handleDownload}
/>
  </>) }
  </Paper>
)}

            {/* Visualization View */}
            {activeTab === 1 && <RetrievalResults retrievalResults={retrievalResults} />}          </>
        )}

            {activeTab === 2 && (
              <ResultsSummary batchId={batch_id} />
        )}

        {/* Row Details */}
        {selectedRow && <RowDetails row={selectedRow} onClose={handleCloseDetails} />}
      </Box>
    </Box>
  );
}

export default ResultsPage;